import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Compass, Bell, Mail, Trophy, User, ChevronDown, ChevronUp,
  Star, X, MessageSquare, Users, Bookmark, Settings,
  LogOut, Sparkles, MessagesSquare, Megaphone, BookOpen, ImageIcon,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Primærmeny – fast, øverst
const PRIMARY_STATIC = [
  { key: "home",      label: "Hjem",         icon: Home,     to: "/" },
  { key: "profil",    label: "Min profil",   icon: User,     dyn: "profile" },
  { key: "meldinger", label: "Meldinger",    icon: Mail,     to: "/meldinger" },
  { key: "varsler",   label: "Varsler",      icon: Bell,     to: "/varsler" },
  { key: "oppdag",    label: "Oppdag",       icon: Compass,  to: "/?view=oppdag" },
  { key: "innst",     label: "Innstillinger", icon: Settings, to: "/innstillinger" },
];

const SECONDARY = [
  { key: "skapere",     label: "Skapere",     icon: Sparkles,       to: "/skapere" },
  { key: "forum",       label: "Forum",       icon: MessageSquare,  to: "/forum" },
  { key: "grupper",     label: "Grupper",     icon: Users,          to: "/grupper" },
  { key: "konk",        label: "Konkurranser", icon: Trophy,        to: "/konkurranser" },
  { key: "chat",        label: "Felleschat",  icon: MessagesSquare, to: "/chat" },
  { key: "annonser",    label: "Annonser",    icon: Megaphone,      to: "/annonser" },
  { key: "noveller",    label: "Noveller",    icon: BookOpen,       to: "/noveller" },
  { key: "galleri",     label: "Galleri",     icon: ImageIcon,      to: "/albums" },
  { key: "bokmerker",   label: "Bokmerker",   icon: Bookmark,       to: "/bookmarks" },
];

/**
 * BetaSidebar — venstre navigasjon.
 * - Desktop (≥lg): fast sticky kolonne.
 * - Mobil (<lg): slide-in-drawer, styrt via `drawerOpen` + `onCloseDrawer` props.
 */
export default function BetaSidebar({ drawerOpen = false, onCloseDrawer = () => {} }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Swipe-til-venstre for å lukke drawer
  const closeStartX = React.useRef(null);
  const closeStartY = React.useRef(null);
  const onDrawerTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    closeStartX.current = t.clientX;
    closeStartY.current = t.clientY;
  };
  const onDrawerTouchMove = (e) => {
    if (closeStartX.current == null) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = t.clientX - closeStartX.current;
    const dy = Math.abs(t.clientY - (closeStartY.current || 0));
    if (dx < -55 && dy < 50) {
      onCloseDrawer();
      closeStartX.current = null;
    }
  };
  const onDrawerTouchEnd = () => { closeStartX.current = null; };

  useEffect(() => {
    api.get("/beta-favorites").then((r) => setFavorites(r.data?.favorites || [])).catch(() => {});
  }, []);

  // Lukk drawer ved ESC
  useEffect(() => {
    if (!drawerOpen) return;
    const h = (e) => { if (e.key === "Escape") onCloseDrawer(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [drawerOpen, onCloseDrawer]);

  // Lukk drawer ved rute-endring
  useEffect(() => {
    if (drawerOpen) onCloseDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const profileTo = user?.username ? `/profile/${user.username}` : "/";
  const skaperTo  = user?.username ? `/skaper/${user.username}` : "/";

  const primary = [];
  for (const item of PRIMARY_STATIC) {
    const to = item.dyn === "profile" ? profileTo : item.to;
    primary.push({ ...item, to });
    if (item.key === "profil" && user?.is_creator) {
      primary.push({
        key: "skaper", label: "Min skaperprofil", icon: Star, to: skaperTo,
      });
    }
  }

  const isActive = (to) => {
    if (!to) return false;
    const [path, query] = to.split("?");
    if (location.pathname !== path) return false;
    return query ? location.search === `?${query}` : !location.search;
  };

  const removeFav = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.delete(`/beta-favorites/${id}`);
      setFavorites((prev) => prev.filter((f) => f.id !== id));
    } catch {
      toast.error(t("beta_nav.fav_remove_failed"));
    }
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      toast.success(t("beta_nav.logged_out"));
      navigate("/");
    } catch {
      toast.error(t("beta_nav.logout_failed"));
      setLoggingOut(false);
    }
  };

  const navItem = (item, isPrimary = true) => {
    const Icon = item.icon;
    const active = isActive(item.to);
    return (
      <Link
        key={item.key}
        to={item.to}
        className={`flex items-center gap-3 px-3 py-2 rounded-md transition ${
          active
            ? "bg-[var(--magenta)]/20 text-[var(--magenta)]"
            : "text-[var(--cream-soft)] hover:bg-white/5 hover:text-[var(--cream)]"
        }`}
        data-testid={`beta-sidebar-${item.key}`}
      >
        <Icon
          className={`h-${isPrimary ? "5" : "4"} w-${isPrimary ? "5" : "4"} ${active ? "fill-current" : ""}`}
          strokeWidth={active ? 2.2 : 1.6}
        />
        <span className={`${isPrimary ? "text-sm" : "text-xs"} ${active ? "font-fraunces italic" : "font-manrope"}`}>
          {t(`beta_nav.${item.key}`, item.label)}
        </span>
      </Link>
    );
  };

  const content = (
    <>
      <nav className="flex flex-col gap-0.5">
        {primary.map((item) => navItem(item, true))}
        <button
          onClick={() => {
            onCloseDrawer && onCloseDrawer();
            if (location.pathname === "/") window.dispatchEvent(new Event("beta-tour-start"));
            else { sessionStorage.setItem("beta-tour-pending", "1"); navigate("/"); }
          }}
          className="flex items-center gap-3 px-3 py-2 rounded-md transition text-[var(--cream-soft)] hover:bg-white/5 hover:text-[var(--cream)] text-left"
          data-testid="beta-sidebar-tour"
        >
          <Compass className="h-5 w-5" strokeWidth={1.6} />
          <span className="text-sm font-manrope">{t("beta_tour.restart", "Omvisning")}</span>
        </button>
        <button
          onClick={handleLogout}
          disabled={loggingOut}
          className="flex items-center gap-3 px-3 py-2 rounded-md transition text-[var(--cream-soft)] hover:bg-red-500/15 hover:text-red-300 text-left"
          data-testid="beta-sidebar-logout"
        >
          <LogOut className="h-5 w-5" strokeWidth={1.6} />
          <span className="text-sm font-manrope">
            {loggingOut ? t("beta_nav.logging_out") : t("beta_nav.logout")}
          </span>
        </button>
      </nav>

      <div className="mt-4 pt-3 border-t border-white/5">
        <button
          onClick={() => setShowMore((v) => !v)}
          className="flex items-center gap-2 w-full px-3 py-2 text-[var(--cream-soft)] hover:text-[var(--cream)] transition"
          data-testid="beta-sidebar-more-toggle"
        >
          {showMore ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="font-meta text-[10px] uppercase tracking-[0.25em]">
            {showMore ? t("beta_nav.less") : t("beta_nav.more")}
          </span>
        </button>
        {showMore && (
          <nav className="flex flex-col gap-0.5 mt-1">
            {SECONDARY.map((item) => navItem(item, false))}
          </nav>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-white/5">
        <div className="px-3 pb-2 flex items-center gap-1.5">
          <Star className="h-3 w-3 text-[var(--magenta)]" />
          <span className="font-meta text-[9px] uppercase tracking-[0.25em] text-[var(--cream-faint)]">
            {t("beta_nav.favorites")}
          </span>
        </div>
        {favorites.length === 0 ? (
          <div className="px-3 py-2 text-xs text-[var(--cream-faint)]/70 italic">
            {t("beta_nav.fav_hint")}
          </div>
        ) : (
          <nav className="flex flex-col gap-0.5">
            {favorites.slice(0, 10).map((f) => (
              <Link
                key={f.id}
                to={f.href || "#"}
                className="group flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/5 transition"
                data-testid={`beta-sidebar-fav-${f.id}`}
              >
                {f.image_url ? (
                  <img src={f.image_url} alt="" className="w-7 h-7 rounded-full object-cover ring-1 ring-[var(--magenta)]/40" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-[10px] text-[var(--cream)]">
                    {(f.label || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[var(--cream)] truncate">{f.label || f.target_id}</div>
                  {f.sublabel && (
                    <div className="font-meta text-[9px] text-[var(--cream-faint)] truncate uppercase tracking-wider">
                      {f.sublabel}
                    </div>
                  )}
                </div>
                <button
                  onClick={(e) => removeFav(f.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-[var(--cream-faint)] hover:text-red-400 transition"
                  title={t("beta_nav.remove")}
                >
                  <X className="h-3 w-3" />
                </button>
              </Link>
            ))}
            {favorites.length > 10 && (
              <div className="px-3 py-1 text-[10px] text-[var(--cream-faint)]">
                +{favorites.length - 10} til
              </div>
            )}
          </nav>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop: fast sticky kolonne */}
      <aside
        className="hidden lg:flex flex-col sticky top-4 self-start pt-1 pb-4 px-2 gap-1"
        style={{ maxHeight: "calc(100vh - 20px)", overflowY: "auto" }}
        data-testid="beta-sidebar"
      >
        {content}
      </aside>

      {/* Mobil: slide-in-drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-200 ${
          drawerOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden={!drawerOpen}
      >
        {/* Overlay */}
        <button
          onClick={onCloseDrawer}
          aria-label={t("beta_nav.close_menu")}
          className="absolute inset-0 bg-black/60"
          data-testid="beta-drawer-overlay"
        />
        {/* Drawer-panel */}
        <aside
          className={`absolute left-0 top-0 h-full w-[80%] max-w-[320px] bg-[#141116] shadow-2xl overflow-y-auto px-3 py-4 transition-transform duration-200 ${
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          data-testid="beta-sidebar-drawer"
          onTouchStart={onDrawerTouchStart}
          onTouchMove={onDrawerTouchMove}
          onTouchEnd={onDrawerTouchEnd}
          onTouchCancel={onDrawerTouchEnd}
        >
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="font-fraunces italic text-xl text-[var(--cream)]">{t("beta_nav.menu")}</span>
            <button
              onClick={onCloseDrawer}
              className="p-1.5 rounded-md hover:bg-white/5 text-[var(--cream-soft)]"
              aria-label={t("beta_nav.close")}
              data-testid="beta-drawer-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {content}
        </aside>
      </div>
    </>
  );
}
